import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const StaffList = () => {
    const [staff, setStaff] = useState([]);
    const [filteredStaff, setFilteredStaff] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${BASE_URL}staff`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setStaff(data.data);
                    setFilteredStaff(data.data);
                } else {
                    alert('Error fetching staff data');
                }
            })
            .catch(error => {
                console.error('Error fetching staff:', error);
                alert('Failed to fetch staff data. Please try again later.');
            });
    }, []);

    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);

        const filtered = staff.filter(person =>
            person.name.toLowerCase().includes(term.toLowerCase()) ||
            person.email.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredStaff(filtered);
    };

    const handleEdit = (id) => {
        navigate(`/EditStaff/${id}`);
    };

    const handleAdd = () => {
        navigate('/AddStaff');
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this staff member?")) {
            fetch(`${BASE_URL}staff/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        setStaff(staff.filter(person => person.id !== id));
                        setFilteredStaff(filteredStaff.filter(person => person.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting staff:', error));
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text('Staff List', 14, 10);

        const tableData = filteredStaff.map(person => [
            person.name || 'N/A',
            person.number || 'N/A',
            person.reference_name || 'N/A',
            person.reference_number || 'N/A',
            person.address || 'N/A',
            person.aadhaar_card_image ? 'Available' : 'N/A',
            person.pan_card_image ? 'Available' : 'N/A',
        ]);

        const headers = [
            'Name', 'Number', 'Ref Name', 'Ref Number', 'Address', 'Aadhaar Image', 'PAN Image'
        ];

        doc.autoTable({
            head: [headers],
            body: tableData,
            startY: 20,
            theme: 'grid',
            headStyles: {
                fillColor: [22, 160, 133],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'center',
            },
            bodyStyles: {
                halign: 'center',
            },
            margin: { top: 20 },
            didDrawPage: function (data) {
                doc.setFontSize(10);
                doc.text(`Page ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
            },
        });

        doc.save('staff_list.pdf');
    };

    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredStaff.map(person => ({
            Name: person.name || 'N/A',
            Email: person.email || 'N/A',
            Number: person.number || 'N/A',
            Location: person.inside_outside || 'N/A',
            'Reference Name': person.reference_name || 'N/A',
            'Reference Number': person.reference_number || 'N/A',
            Address: person.address || 'N/A',
            Aadhaar: person.aadhaar_card_image ? `${BASE_URL}${person.aadhaar_card_image}` : 'N/A',
            PAN: person.pan_card_image ? `${BASE_URL}${person.pan_card_image}` : 'N/A'
        })));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Staff List");
        XLSX.writeFile(workbook, 'staff_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Staff List</h3>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6"></div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={handleAdd}>Add Staff</button>
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                        {/* <button className="btn btn-secondary ms-2" onClick={handleExportExcel}>Export to Excel</button> */}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="searchTerm" className="form-label">Search by Name or Email</label>
                                    <input
                                        type="text"
                                        id="searchTerm"
                                        className="form-control"
                                        placeholder="Search staff name or email"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>NAME</th>
                                                <th>NUMBER</th>
                                                <th>LOCATION</th>
                                                <th>SALARY</th>
                                                <th>ADDRESS</th>
                                                <th>AADHAAR</th>
                                                <th>PAN</th>
                                                <th className="text-end">ACTIONS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStaff.length > 0 ? (
                                                filteredStaff.map(person => (
                                                    <tr key={person.id}>
                                                        <td>{person.name || 'N/A'}</td>
                                                        <td>{person.number || 'N/A'}</td>
                                                        <td>{person.inside_outside || 'N/A'}</td>
                                                        <td>{person.reference_number || 'N/A'}</td>
                                                        <td>{person.address || 'N/A'}</td>
                                                        <td>
                                                            {person.aadhaar_card_image ? (
                                                                <img src={`http://localhost:8000${person.aadhaar_card_image}`} alt="Aadhaar" width="50" />
                                                            ) : 'N/A'}
                                                        </td>
                                                        <td>
                                                            {person.pan_card_image ? (
                                                                <img src={`http://localhost:8000${person.pan_card_image}`} alt="PAN Card" width="50" />
                                                            ) : 'N/A'}
                                                        </td>
                                                        <td className="text-end">
                                                            <button className="btn btn-success" onClick={() => handleEdit(person.id)}>Edit</button>
                                                            <button className="btn btn-danger ms-2" onClick={() => handleDelete(person.id)}>Delete</button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center">No staff found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default StaffList;
